const DEFAULT_RETRY_DELAYS_MS = [0, 700, 2_000];

export async function createStorefrontDesign({
  url = "/api/storefront/designs",
  payload,
  requestId = createRequestId(),
  fetchImpl = globalThis.fetch,
  retryDelaysMs = DEFAULT_RETRY_DELAYS_MS,
  requestTimeoutMs = 45_000,
  sleep = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds)),
  onRetry = () => {},
}) {
  let lastError = new Error("Produksjonsfilene kunne ikke genereres.");

  for (let attempt = 0; attempt < retryDelaysMs.length; attempt += 1) {
    const delayMs = retryDelaysMs[attempt];
    if (delayMs) await sleep(delayMs);
    try {
      const response = await fetchWithTimeout(fetchImpl, url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Navnlapp-Request-Id": requestId,
        },
        body: JSON.stringify({ ...payload, requestId }),
      }, requestTimeoutMs);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(result.error || `Produksjonstjenesten svarte HTTP ${response.status}.`);
        error.statusCode = response.status;
        error.retriable = [408, 425, 429].includes(response.status) || response.status >= 500;
        throw error;
      }
      return { result, attempts: attempt + 1, requestId };
    } catch (error) {
      lastError = normalizeDesignRequestError(error);
      if (lastError.retriable === false || attempt >= retryDelaysMs.length - 1) break;
      onRetry({ attempt: attempt + 1, nextAttempt: attempt + 2, delayMs: retryDelaysMs[attempt + 1], error: lastError });
    }
  }

  throw lastError;
}

function createRequestId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

async function fetchWithTimeout(fetchImpl, url, options, timeoutMs) {
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timeout = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    return await fetchImpl(url, { ...options, ...(controller ? { signal: controller.signal } : {}) });
  } catch (error) {
    const normalized = normalizeDesignRequestError(error);
    normalized.retriable = true;
    throw normalized;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

function normalizeDesignRequestError(error) {
  if (error instanceof Error) return error;
  return new Error(String(error || "Produksjonsfilene kunne ikke genereres."));
}
